import React, { useEffect, useState } from 'react';
import { BarChart3, Download, FileSpreadsheet, Loader2, RefreshCw, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCurrentBodyCorporateId, supabase, Transaction } from '../lib/supabase';
import { getLocalDateString } from '../lib/dateUtils';
import { ExportService } from '../services/ExportService';

type ReportSummary = {
  transactions: number;
  charges: number;
  payments: number;
  netMovement: number;
  outstanding: number;
};

const startOfCurrentMonth = () => {
  const today = new Date();
  return getLocalDateString(new Date(today.getFullYear(), today.getMonth(), 1));
};

const Reports: React.FC = () => {
  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(startOfCurrentMonth);
  const [toDate, setToDate] = useState(getLocalDateString);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<ReportSummary>({ transactions: 0, charges: 0, payments: 0, netMovement: 0, outstanding: 0 });
  const [breakdown, setBreakdown] = useState<Array<{ type: string; count: number; debit: number; credit: number }>>([]);

  const loadReport = async (id = bodyCorporateId) => {
    if (!id) return;
    if (fromDate > toDate) {
      toast.error('The start date must be on or before the end date.');
      return;
    }
    setLoading(true);

    try {
      const [periodResult, ledgerResult] = await Promise.all([
        supabase
          .from('transactions')
          .select('transaction_type, debit_amount, credit_amount')
          .eq('body_corporate_id', id)
          .eq('status', 'posted')
          .gte('transaction_date', fromDate)
          .lte('transaction_date', toDate),
        supabase
          .from('transactions')
          .select('debit_amount, credit_amount')
          .eq('body_corporate_id', id)
          .eq('status', 'posted'),
      ]);

      if (periodResult.error) throw periodResult.error;
      if (ledgerResult.error) throw ledgerResult.error;

      const transactions = (periodResult.data || []) as Pick<Transaction, 'transaction_type' | 'debit_amount' | 'credit_amount'>[];
      const ledgerTransactions = (ledgerResult.data || []) as Pick<Transaction, 'debit_amount' | 'credit_amount'>[];
      const charges = transactions.reduce((total, transaction) => total + Number(transaction.debit_amount || 0), 0);
      const payments = transactions.reduce((total, transaction) => total + Number(transaction.credit_amount || 0), 0);
      const outstanding = ledgerTransactions.reduce(
        (total, transaction) => total + Number(transaction.debit_amount || 0) - Number(transaction.credit_amount || 0),
        0,
      );

      const totalsByType = new Map<string, { count: number; debit: number; credit: number }>();
      transactions.forEach((transaction) => {
        const type = transaction.transaction_type.split('_').join(' ');
        const current = totalsByType.get(type) || { count: 0, debit: 0, credit: 0 };
        current.count += 1;
        current.debit += Number(transaction.debit_amount || 0);
        current.credit += Number(transaction.credit_amount || 0);
        totalsByType.set(type, current);
      });

      setSummary({ transactions: transactions.length, charges, payments, netMovement: charges - payments, outstanding });
      setBreakdown(Array.from(totalsByType.entries())
        .map(([type, values]) => ({ type, ...values }))
        .sort((a, b) => (b.debit + b.credit) - (a.debit + a.credit)));
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Unable to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const id = await getCurrentBodyCorporateId();
      if (!id) {
        toast.error('No body corporate is configured for this user.');
        setLoading(false);
        return;
      }
      setBodyCorporateId(id);
      await loadReport(id);
    };
    initialize();
    // The report is refreshed explicitly after changing its date range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadReport = async (kind: 'transactions' | 'accounts') => {
    if (!bodyCorporateId) return;
    if (fromDate > toDate) {
      toast.error('The start date must be on or before the end date.');
      return;
    }
    setExporting(true);
    try {
      const exporter = new ExportService(bodyCorporateId);
      const csv = kind === 'transactions'
        ? await exporter.exportTransactionsToCSV(undefined, fromDate, toDate)
        : await exporter.exportAccountsToCSV();
      exporter.downloadCSV(csv, `${kind}-report-${getLocalDateString()}.csv`);
      toast.success('Report downloaded');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Unable to download report');
    } finally {
      setExporting(false);
    }
  };

  const currency = (amount: number) => `R ${amount.toFixed(2)}`;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-gray-500">Review scheme financial activity and download report data</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadReport('transactions')} disabled={exporting} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
            <Download size={18} /> Transactions CSV
          </button>
          <button onClick={() => downloadReport('accounts')} disabled={exporting} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
            <FileSpreadsheet size={18} /> Accounts CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <button onClick={() => loadReport()} disabled={loading || !fromDate || !toDate} className="btn-primary flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            Run Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <ReportCard label="Charges Raised" value={currency(summary.charges)} icon={<TrendingUp size={20} />} color="text-red-600" />
        <ReportCard label="Payments Received" value={currency(summary.payments)} icon={<TrendingDown size={20} />} color="text-green-600" />
        <ReportCard label="Net Movement" value={currency(summary.netMovement)} icon={<BarChart3 size={20} />} color={summary.netMovement > 0 ? 'text-red-600' : 'text-green-600'} />
        <ReportCard label="Outstanding Balance" value={currency(summary.outstanding)} icon={<Wallet size={20} />} color={summary.outstanding > 0 ? 'text-red-600' : 'text-green-600'} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Activity by Transaction Type</h2>
            <p className="text-sm text-gray-500">{summary.transactions} posted transactions in the selected period</p>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading report...</div>
        ) : breakdown.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No posted transactions in the selected period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction Type</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Entries</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Charges</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payments / Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {breakdown.map((row) => (
                  <tr key={row.type} className="hover:bg-gray-50">
                    <td className="px-5 py-3 capitalize font-medium">{row.type}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{row.count}</td>
                    <td className="px-5 py-3 text-right text-red-600">{currency(row.debit)}</td>
                    <td className="px-5 py-3 text-right text-green-600">{currency(row.credit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ReportCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className={`flex items-center gap-2 ${color}`}>
      {icon}
      <span className="text-sm font-medium text-gray-500">{label}</span>
    </div>
    <p className="mt-3 text-2xl font-bold text-gray-900">{value}</p>
  </div>
);

export default Reports;
